import { Injector, afterNextRender } from '@angular/core';
import { AbstractControl } from '@angular/forms';

export const FORM_INVALID_CONTROL_SELECTOR =
  'ion-input.ng-invalid, ion-select.ng-invalid, ion-textarea.ng-invalid, ion-checkbox.ng-invalid, ion-toggle.ng-invalid';
export const FORM_ERROR_MESSAGE_SELECTOR = 'ion-text[color="danger"]';
export const FORM_SECTION_ATTRIBUTE = 'data-form-section';

/**
 * Delay before scrolling to the error. Ionic shows its errorText (recomputing the ion-* classes in
 * a rAF) and the appearing error messages change the layout, which interrupts a smooth scroll
 * started too early.
 */
export const FORM_FEEDBACK_SCROLL_DELAY_MS = 50;

/**
 * Shows the validation errors of a form: marks it as touched, recalculates its validity and, once
 * the render has flushed, scrolls to the topmost error inside the given container.
 *
 * The container must be the current page or modal element: querying the whole document is wrong
 * because the Ionic router keeps the previous pages in the DOM.
 *
 * The second markAllAsTouched is required by Ionic: its ValueAccessor patches the control methods
 * (markAsTouched, markAllAsTouched, ...) to recompute the ion-* classes from the Angular ng-*
 * classes in a rAF (setIonicClasses), and the first call runs before the change detection applies
 * ng-touched (with eventCoalescing enabled the change detection is scheduled in a later rAF), so
 * their errorText needs the Ionic classes to be recalculated once the render is done.
 */
export function markFormAsInvalid(form: AbstractControl, container: HTMLElement | null, injector: Injector): void {
  form.markAllAsTouched();
  form.updateValueAndValidity();

  afterNextRender(
    () => {
      form.markAllAsTouched();
      void scrollToTopmostFormError(container);
    },
    { injector },
  );
}

/**
 * Scrolls to the topmost error of the container: the first section with an error and, inside it,
 * the first invalid control or error message (or the section itself when the error has no rendered
 * field, e.g. a company shown as a summary or a missing location).
 *
 * When there are no marked sections the first candidate of the container is used, which covers the
 * modals.
 *
 * Must be called once the render has flushed; it waits a short delay (FORM_FEEDBACK_SCROLL_DELAY_MS)
 * so the error state is already painted before scrolling.
 */
export async function scrollToTopmostFormError(
  container: HTMLElement | null,
  delayMs = FORM_FEEDBACK_SCROLL_DELAY_MS,
): Promise<void> {
  if (!container) return;

  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  const firstErrorSection = Array.from(container.querySelectorAll<HTMLElement>(`[${FORM_SECTION_ATTRIBUTE}]`)).find(
    (section) => !!section.querySelector('.ng-invalid') || !!section.querySelector(FORM_ERROR_MESSAGE_SELECTOR),
  );

  if (firstErrorSection) {
    const invalidControl = firstErrorSection.querySelector(FORM_INVALID_CONTROL_SELECTOR);
    const errorMessage = firstErrorSection.querySelector(FORM_ERROR_MESSAGE_SELECTOR);

    if (invalidControl) {
      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (errorMessage) {
      errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    firstErrorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  const invalidControl = container.querySelector(FORM_INVALID_CONTROL_SELECTOR);
  const errorMessage = container.querySelector(FORM_ERROR_MESSAGE_SELECTOR);

  (invalidControl ?? errorMessage)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
